import { useEffect, useState, useRef } from "react";
import {
  subscribeToTask,
  getTaskData,
  isTaskActive,
  addSyncTask,
  removeSyncTask,
  listActiveTasks,
} from "services/globalSyncPoller";
import {
  extractProgressFromFhir,
  extractProcessedGroups,
} from "config/helpers/syncTaskHelpers";
import generateLabel from "config/helpers/syncLabel";

export function useTaskStatus(taskId) {
  const [isActiveState, setIsActiveState] = useState(() =>
    isTaskActive(taskId)
  );
  const [progress, setProgress] = useState(0);
  const [taskData, setTaskData] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!taskId) return;

    // initialize from cache
    const cached = getTaskData(taskId);
    if (cached) {
      if (cached.error) {
        setTaskData({ error: cached.error });
        setProgress(0);
      } else if (cached.raw) {
        const { progress: p, taskData: td } = extractProgressFromFhir(
          cached.raw
        );
        setProgress(p);
        setTaskData(td);
      }
    }

    setIsActiveState(isTaskActive(taskId));

    const unsub = subscribeToTask(taskId, (err, fhir) => {
      if (!mountedRef.current) return;
      if (err) {
        setTaskData({ error: err });
        setProgress(0);
        return;
      }
      const { progress: p, taskData: td } = extractProgressFromFhir(fhir);
      setProgress(p);
      setTaskData(td);
      setIsActiveState(isTaskActive(taskId));
    });

    return () => unsub();
  }, [taskId]);

  return {
    isActive: isActiveState,
    progress,
    taskData,
    // helper functions to control the poller from UI if desired
    startTracking: () => addSyncTask(taskId),
    stopTracking: () => removeSyncTask(taskId),
  };
}

/**
 * Hook to poll for all active sync tasks and return their processed data.
 * Replaces the manual polling in Notification.jsx
 */
export function useActiveSyncTasks(pollInterval = 3000) {
  const [tasks, setTasks] = useState([]);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let timer = null;

    const refresh = () => {
      if (!mountedRef.current) return;

      try {
        const ids = listActiveTasks ? listActiveTasks() : [];
        const currentTasks = (ids || []).map((id) => {
          const data = getTaskData ? getTaskData(id) : null;

          // Use shared helper to extract status/progress
          const { progress, taskData, status } = extractProgressFromFhir(data?.raw);

          // Process groups using shared helper
          const processedGroups = extractProcessedGroups(taskData, progress);

          // Determine label
          let label = null;
          if (taskData) {
            const groupCodePick =
              taskData.currentTaskGroupCode ||
              (taskData.taskGroups?.[0]?.code) ||
              null;
            label = groupCodePick
              ? generateLabel(groupCodePick)
              : data?.raw?.entry?.[0]?.resource?.description ||
              data?.raw?.entry?.[0]?.resource?.code || null;
          }

          return {
            id,
            progress,
            label,
            status,
            processedGroups,
            subtaskCode: data?.subtaskCode || null,
            subtaskProgress: typeof data?.subtaskProgress === "number" ? data.subtaskProgress : null,
            raw: data?.raw
          };
        });

        setTasks(currentTasks);
      } catch (e) {
        console.warn("useActiveSyncTasks: failed to read active sync tasks", e);
      }

      if (mountedRef.current) {
        timer = setTimeout(refresh, pollInterval);
      }
    };

    refresh();

    return () => {
      mountedRef.current = false;
      if (timer) clearTimeout(timer);
    };
  }, [pollInterval]);

  return tasks;
}

/**
 * Hook to get the first active task found (for RightDrawer)
 */
export function useFirstActiveTask(pollInterval = 2000) {
  const tasks = useActiveSyncTasks(pollInterval);
  return tasks.length > 0 ? tasks[0] : null;
}

export default useTaskStatus;
